import React, { useCallback, useEffect, useMemo, useState, Fragment, useContext } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { PrimaryButton } from '@fluentui/react';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { createBrandsGridForUiNext } from '@bingads-webui-campaign-react/brand-page';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import { withGlobalFilter } from '@bingads-webui-campaign-react/global-filter-context';
import { StyledGridViewWrapper } from '@bingads-webui-campaign-react/grid-view-wrapper';
import { StyledTablePageContainer } from '@bingads-webui-campaign-react/table-page-container';
import { withTablePageStateManager } from '@bingads-webui-campaign-react/table-page-state-manager';
import { withCurrentExpandedScope } from '@bingads-webui-campaign-react/use-current-scope-entity';
import { Activity, Scenario, ActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { getToolbarConfig } from './toolbar-config';
import { AddBrandPanel } from './add-brand-panel';
import { AddBrandPanelV2 } from './add-brand-panel-v2';
import { DataTile } from './data-tile';

import { getOverviewStyles } from '../style';
import placeholderImage from '../../media/placeholder.svg';

const StyledGridViewWrapperWithFilterContext = withGlobalFilter(StyledGridViewWrapper);

const logScope = 'SimplifiedShoppingForBrands';

const NewSummaryPage = (props) => {
  const {
    classes,
    i18n,
    serverContext,
    scope,
    preferenceService,
    permissions,
    crossPageSelectPlugin,
    toolbarPlugin,
    onGridInjected,
    showTopOfGridPanel,
    currentCampaign,
    currentAdGroup,
    siteMap,
    perfMarker,
    changeLayout,
    renderToolbarContainer,
  } = props;

  const activity = useContext(ActivityContext);

  const [gridViewConfig, setGridViewConfig] = useState({
    GRID_EVENT_NAMES: {},
    gridView: null,
    brandGrid: {
      rendered: false,
      fetchGridData: false,
    },
    backboneGridView: null,
  });
  const [productFeedGrid, setProductFeedGrid] = useState(null);
  const [brandsList, setBrandsList] = useState([]);
  const [isAddBrandPanelOpen, setIsAddBrandPanelOpen] = useState(false);
  const [refreshDataTile, setRefreshDataTile] = useState(true);
  const isAllBrandsVerified = useMemo(() => _.every(brandsList, brand => brand.Status === 1), [brandsList]);

  const { MranSimplificationV2 } = permissions.dynamic;

  const setLayout = useCallback(() => {
    changeLayout({
      tab: {
        mode: 'collapsed',
        showDatePicker: false,
        disableCompare: true,
      },
    }, true, true);
  }, [changeLayout]);

  const showAddBrandPanel = useCallback(() => {
    activity.trace(`${logScope}|NewSummaryPage|open add brand panel`);
    setIsAddBrandPanelOpen(true);
  }, [activity, setIsAddBrandPanelOpen]);

  const fetchDataCallback = useCallback((brands) => {
    activity.trace(`${logScope}|NewSummaryPage|brands length: ${_.size(brands)}`);
    setBrandsList(brands);
  }, [setBrandsList, activity]);

  useEffect(() => {
    if (gridViewConfig.gridView) {
      gridViewConfig.gridView.on(gridViewConfig.GRID_EVENT_NAMES.SHOW_ADD_BRAND_PANEL, showAddBrandPanel);
      gridViewConfig.gridView.on(gridViewConfig.GRID_EVENT_NAMES.DATA_REFRESHED, setRefreshDataTile);
    }
    return () => {
      if (gridViewConfig.gridView) {
        gridViewConfig.gridView.off(gridViewConfig.GRID_EVENT_NAMES.SHOW_ADD_BRAND_PANEL, showAddBrandPanel);
        gridViewConfig.gridView.off(gridViewConfig.GRID_EVENT_NAMES.DATA_REFRESHED, setRefreshDataTile);
      }
    };
  }, [gridViewConfig, showAddBrandPanel]);

  useEffect(() => {
    activity.trace(`${logScope}|NewSummaryPage|loaded`);
    setLayout();
  }, [activity, setLayout]);

  useEffect(() => {
    const brandsGridPromise = createBrandsGridForUiNext({
      crossPageSelectPlugin,
      serverContext,
      scope,
      preferenceService,
      permissions,
      toolbarPlugin,
      currentCampaign,
      currentAdGroup,
      siteMap,
      perfMarker,
      fetchDataCallback,
      hideSubBrand: true,
    });
    const BrandsGridView = React.lazy(() => { // eslint-disable-line arrow-body-style
      return brandsGridPromise.then(({ gridView }) => {
        gridView.overrideWith = showTopOfGridPanel; // eslint-disable-line no-param-reassign
        onGridInjected({
          backboneGridView: gridView,
          deselectAll: gridView.grid.gridView.deselectAll.bind(gridView.grid.gridView),
        });
        setGridViewConfig({
          GRID_EVENT_NAMES: gridView.EVENT_NAMES,
          gridView: gridView.grid.gridView,
          brandGrid: gridView,
          backboneGridView: gridView,
        });

        return {
          default: () => (<StyledGridViewWrapperWithFilterContext gridView={gridView} />),
        };
      });
    });

    setProductFeedGrid(<BrandsGridView />);
  }, [setProductFeedGrid, onGridInjected, showTopOfGridPanel, crossPageSelectPlugin,
    serverContext, scope, preferenceService, permissions, toolbarPlugin, currentCampaign,
    currentAdGroup, siteMap, perfMarker, fetchDataCallback]);

  const stickyTopOfGridContent = useMemo(() => (
    <Fragment>
      {renderToolbarContainer()}
    </Fragment>
  ), [renderToolbarContainer]);

  const topOfGridContent = useMemo(() => (
    <div className={classNames([classes.overviewContainer])}>
      <div className={classNames([classes.subTitle])}>
        { i18n.getString(_TL_('Boost product sales by partnering with retailers to cooperatively bid on shopping campaigns.')) }
      </div>
      {!isAllBrandsVerified || _.size(brandsList) === 0 ?
        <div className={classNames([classes.placeholderContainer, 'placeholder-container'])}>
          <img className={classNames([classes.placeholderImage])} src={placeholderImage} alt="placeholder" />
          <p className={classNames([classes.addBrandsTip])}>
            { !isAllBrandsVerified ?
            i18n.getString(MranSimplificationV2 ? _TL_('Product matching can take up to 10 hours.') : _TL_('We’re still processing your brands. This may take up to 24 hours.')) :
            i18n.getString(_TL_('Start matching with product catalogs by adding brands.'))
            }
          </p>
          {_.size(brandsList) <= 0 && <PrimaryButton
            className={classNames([classes.addButton, 'add-button-in-placeholder'])}
            iconProps={{ iconName: 'Add' }}
            text={i18n.getString(_TL_('Add brands'))}
            onClick={showAddBrandPanel}
          />}
        </div>
      :
        <div className={classNames([classes.tileContainer])}>
          <DataTile
            i18n={i18n}
            scope={scope}
            shouldRefresh={refreshDataTile}
            setShouldRefresh={setRefreshDataTile}
            activity={activity}
            logScope={logScope}
            brandsList={brandsList}
            permissions={permissions}
          />
        </div>
      }
    </div>
  ), [classes, i18n, isAllBrandsVerified, brandsList, MranSimplificationV2, showAddBrandPanel, scope, refreshDataTile, activity, permissions]);

  return (
    <React.Suspense fallback={<ProgressiveLoadingBlock />}>
      <StyledTablePageContainer
        topOfGridContent={topOfGridContent}
        stickyTopOfGridContent={_.size(brandsList) > 0 ? stickyTopOfGridContent : null}
        gridView={productFeedGrid}
        backboneGridView={gridViewConfig.backboneGridView}
        customClasses={{
          tablePageContainer: 'brand-page',
          gridViewContainer: `brand-table ${_.size(brandsList) > 0 ? '' : 'brand-table-hide'}`,
        }}
      />
      { MranSimplificationV2 ? <AddBrandPanelV2
        isOpen={isAddBrandPanelOpen}
        onClose={() => setIsAddBrandPanelOpen(false)}
        brandGrid={gridViewConfig.brandGrid}
        i18n={i18n}
        scope={scope}
        activity={activity}
        logScope={logScope}
        setRefreshDataTile={setRefreshDataTile}
        perfMarker={perfMarker}
        brandsList={brandsList}
      /> : <AddBrandPanel
        isOpen={isAddBrandPanelOpen}
        onClose={() => setIsAddBrandPanelOpen(false)}
        brandGrid={gridViewConfig.brandGrid}
        i18n={i18n}
        scope={scope}
        activity={activity}
        logScope={logScope}
        setRefreshDataTile={setRefreshDataTile}
        perfMarker={perfMarker}
        brandsList={brandsList}
      /> }
    </React.Suspense>
  );
};

NewSummaryPage.propTypes = {
  classes: PropTypes.shape({
    overviewContainer: PropTypes.string.isRequired,
    subTitle: PropTypes.string.isRequired,
    placeholderImage: PropTypes.string.isRequired,
    placeholderContainer: PropTypes.string.isRequired,
    addBrandsTip: PropTypes.string.isRequired,
    addButton: PropTypes.string.isRequired,
    tileContainer: PropTypes.string.isRequired,
  }),
  serverContext: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  scope: PropTypes.shape({}).isRequired,
  changeLayout: PropTypes.func.isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      MranSimplificationV2: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  currentCampaign: PropTypes.shape({}).isRequired,
  currentAdGroup: PropTypes.shape({}).isRequired,
  renderToolbarContainer: PropTypes.func.isRequired,
  crossPageSelectPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  toolbarPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  panelView: PropTypes.shape({
    render: PropTypes.func,
  }).isRequired,
  onGridInjected: PropTypes.func.isRequired,
  showTopOfGridPanel: PropTypes.func.isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
};

NewSummaryPage.defaultProps = {
  classes: {},
};

const NewSummaryPageWithLogContext = props => (
  <Scenario name="SimplifiedShoppingForBrands">
    <Activity name="SimplifiedShoppingForBrandsActivity">
      <NewSummaryPage {...props} />
    </Activity>
  </Scenario>
);

export const StyledNewSummaryPage = _.compose(
  WrappedComponent => withDefaultStyles(WrappedComponent, getOverviewStyles),
  withCurrentExpandedScope,
  withTablePageStateManager({ getToolbarConfig })
)(NewSummaryPageWithLogContext);
